import React from 'react';

import { css } from '@emotion/css';

import {
  LogsDrilldownDefaultColumnsLogsDefaultColumnsLabel,
  LogsDrilldownDefaultColumnsLogsDefaultColumnsLabels,
  LogsDrilldownDefaultColumnsSpec,
  useCreateLogsDrilldownDefaultColumnsMutation,
  useReplaceLogsDrilldownDefaultColumnsMutation,
} from '@grafana/api-clients/rtkq/logsdrilldown/v1beta1';
import { GrafanaTheme2 } from '@grafana/data';
import { t } from '@grafana/i18n';
import { Button, useStyles2 } from '@grafana/ui';

import { useDefaultColumnsContext } from './Context';
import { DefaultColumnsValidationState } from './types';
import { reportAppInteraction, USER_EVENTS_ACTIONS, USER_EVENTS_PAGES } from 'services/analytics';
import { logger } from 'services/logger';
import { getRTKQErrorContext, narrowRTKQError } from 'services/narrowing';

export function Submit() {
  const { dsUID, metadata, records, validation } = useDefaultColumnsContext();
  const [create, { error: createError }] = useCreateLogsDrilldownDefaultColumnsMutation();
  const [update, { error: updateError }] = useReplaceLogsDrilldownDefaultColumnsMutation();
  const styles = useStyles2(getStyles, validation.isInvalid);
  const createNewRecord = metadata === null;
  if (!records || !dsUID) {
    return null;
  }

  if (createError) {
    const error = narrowRTKQError(createError);
    logger.error(
      new Error('DefaultColumnsSubmit::createNewRecord'),
      error ? getRTKQErrorContext(error) : { msg: 'DefaultColumnsSubmit:createNewRecord error' }
    );
  }
  if (updateError) {
    const error = narrowRTKQError(updateError);
    logger.error(
      new Error('DefaultColumnsSubmit::updateRecord'),
      error ? getRTKQErrorContext(error) : { msg: 'DefaultColumnsSubmit:updateRecord error' }
    );
  }

  return (
    <Button
      variant={'primary'}
      tooltip={getTooltip(validation)}
      disabled={!validation.hasPendingChanges || validation.isInvalid}
      className={styles.button}
      onClick={() => {
        if (dsUID && records) {
          const updated: LogsDrilldownDefaultColumnsSpec = {
            records: records.map((r) => {
              const labels: LogsDrilldownDefaultColumnsLogsDefaultColumnsLabels = r.labels.filter(
                (label): label is LogsDrilldownDefaultColumnsLogsDefaultColumnsLabel => !!label.value && !!label.key
              );
              return {
                labels,
                columns: r.columns,
              };
            }),
          };

          if (createNewRecord) {
            create({
              pretty: 'true',
              logsDrilldownDefaultColumns: {
                metadata: {
                  name: dsUID,
                },
                apiVersion: 'logsdrilldown.grafana.app/v1beta1',
                kind: 'LogsDrilldownDefaultColumns',
                spec: updated,
              },
            });
          } else {
            update({
              pretty: 'true',
              name: dsUID,
              logsDrilldownDefaultColumns: {
                metadata: {
                  name: dsUID,
                  resourceVersion: metadata.resourceVersion,
                },
                apiVersion: 'logsdrilldown.grafana.app/v1beta1',
                kind: 'LogsDrilldownDefaultColumns',
                spec: updated,
              },
            });
          }

          reportAppInteraction(
            USER_EVENTS_PAGES.default_columns_config,
            USER_EVENTS_ACTIONS.default_columns_config.save
          );
        }
      }}
    >
      {createNewRecord
        ? t('components.app-config.default-columns.submit.create-default-columns', 'Create default columns')
        : t('components.app-config.default-columns.submit.update-default-columns', 'Update default columns')}
    </Button>
  );
}

const getStyles = (theme: GrafanaTheme2, isInvalid: boolean) => ({
  button: css({
    borderColor: isInvalid ? theme.colors.error.border : undefined,
  }),
});

const getTooltip = (validation: DefaultColumnsValidationState): string | undefined => {
  if (!validation.hasPendingChanges) {
    return 'No changes detected';
  }
  if (validation.hasDuplicates) {
    return 'Duplicates detected';
  }
  if (validation.hasInvalidRecords) {
    return 'Invalid records detected';
  }

  return undefined;
};
